/**
 * sampleTrips.js — Anonymized realistic example ETC trips for the
 * TollTripStatusChecker sample-report preview.
 *
 * IMPORTANT: every trip below is a fictional commute pattern. We never
 * pretend this is real customer data. The widget clearly labels the
 * preview "SAMPLE · Not your real data".
 *
 * Each trip mirrors the SHAPE of a real MEISAI CSV row (entry date /
 * time, exit date / time, entry IC, exit IC, base toll, discount,
 * final toll, vehicle class 車種, OBU last-4, ETC card last-4) so
 * visitors see exactly the column structure JTR delivers in real PDFs.
 *
 * Trips also carry two SMART-AUDIT flags that JTR adds on top of the
 * raw MEISAI data — these are the "advanced features" we surface in
 * the daily email summary:
 *
 *   • `authorized`  — true if the plate is on the customer's
 *     authorized-vehicle list. false flags an unrecognised vehicle.
 *   • `manualToll`  — true if the trip was paid by HANDING the ETC card
 *     to a booth operator (手渡し) instead of driving through a wireless
 *     ETC lane. CRITICAL real-world detail: on a manual handoff the
 *     on-board unit (OBU) is NOT read and the license plate is NOT
 *     recorded — the operator manually selects the vehicle class. The
 *     ETC CARD is still charged, so the card last-4 IS present. JTR's
 *     daily email surfaces this explicitly because a manual handoff
 *     usually indicates a procedural exception.
 *
 * Vehicle class taxonomy follows the official MEISAI 車種 field:
 * 軽自動車 · 普通車 · 中型車 · 大型車 · 特大車.
 */

// ── Trip template pool ──────────────────────────────────────────────
// A broad, varied pool so long date ranges can surface a substantial,
// realistic-looking report (hundreds of thousands of yen) rather than
// repeating the same three commutes. Mix of short commutes, mid-range
// business runs, weekend leisure, long-distance intercity hauls, plus a
// couple of MANUAL-HANDOFF (手渡し) rows.
//
// Manual rows: obuLast4 = '' and plate = '' (genuinely not recorded on a
// handoff), but cardLast4 IS set (the card is still charged).
const POOL = [
  // Daily commute (普通車, wireless ETC, authorized)
  { icJa: ['横浜青葉', '川崎浮島JCT'], icEn: ['Yokohama-Aoba', 'Kawasaki-Ukishima JCT'], entryTime: '07:42', exitTime: '08:19', vehicleClassJa: '普通車', vehicleClassEn: 'Standard', obuLast4: '7821', cardLast4: '3962', plate: '横浜 33-XX', base: 1340, discount: 0, final: 1340, authorized: true, manualToll: false },
  { icJa: ['川崎浮島JCT', '横浜青葉'], icEn: ['Kawasaki-Ukishima JCT', 'Yokohama-Aoba'], entryTime: '18:11', exitTime: '18:48', vehicleClassJa: '普通車', vehicleClassEn: 'Standard', obuLast4: '7821', cardLast4: '3962', plate: '横浜 33-XX', base: 1340, discount: 0, final: 1340, authorized: true, manualToll: false },
  { icJa: ['用賀', '東京'], icEn: ['Yoga', 'Tokyo'], entryTime: '09:03', exitTime: '09:18', vehicleClassJa: '普通車', vehicleClassEn: 'Standard', obuLast4: '7821', cardLast4: '3962', plate: '横浜 33-XX', base: 410, discount: 0, final: 410, authorized: true, manualToll: false },

  // Business runs (authorized)
  { icJa: ['東京', '富士川SA'], icEn: ['Tokyo', 'Fujikawa SA'], entryTime: '10:21', exitTime: '12:04', vehicleClassJa: '普通車', vehicleClassEn: 'Standard', obuLast4: '4408', cardLast4: '4471', plate: '品川 500-YY', base: 3870, discount: 0, final: 3870, authorized: true, manualToll: false },
  { icJa: ['川口JCT', '宇都宮'], icEn: ['Kawaguchi JCT', 'Utsunomiya'], entryTime: '22:48', exitTime: '00:34', vehicleClassJa: '普通車', vehicleClassEn: 'Standard', obuLast4: '4408', cardLast4: '4471', plate: '品川 500-YY', base: 3220, discount: 322, final: 2898, authorized: true, manualToll: false },

  // Weekend / leisure (軽自動車, authorized)
  { icJa: ['横浜青葉', '河口湖'], icEn: ['Yokohama-Aoba', 'Kawaguchiko'], entryTime: '08:14', exitTime: '10:02', vehicleClassJa: '軽自動車', vehicleClassEn: 'Kei', obuLast4: '2055', cardLast4: '6688', plate: '横浜 500-ZZ', base: 2740, discount: 0, final: 2740, authorized: true, manualToll: false },
  { icJa: ['東名川崎', '御殿場'], icEn: ['Tomei-Kawasaki', 'Gotemba'], entryTime: '09:30', exitTime: '10:48', vehicleClassJa: '軽自動車', vehicleClassEn: 'Kei', obuLast4: '2055', cardLast4: '6688', plate: '横浜 500-ZZ', base: 1860, discount: 186, final: 1674, authorized: true, manualToll: false },
  { icJa: ['海老名', '小田原'], icEn: ['Ebina', 'Odawara'], entryTime: '13:22', exitTime: '14:04', vehicleClassJa: '普通車', vehicleClassEn: 'Standard', obuLast4: '4408', cardLast4: '4471', plate: '品川 500-YY', base: 920, discount: 0, final: 920, authorized: true, manualToll: false },

  // Long-distance intercity hauls (higher value, authorized)
  { icJa: ['東京', '名古屋'], icEn: ['Tokyo', 'Nagoya'], entryTime: '06:30', exitTime: '10:12', vehicleClassJa: '中型車', vehicleClassEn: 'Medium', obuLast4: '4408', cardLast4: '4471', plate: '品川 500-YY', base: 7100, discount: 710, final: 6390, authorized: true, manualToll: false },
  { icJa: ['名古屋', '吹田JCT'], icEn: ['Nagoya', 'Suita JCT'], entryTime: '13:05', exitTime: '15:48', vehicleClassJa: '中型車', vehicleClassEn: 'Medium', obuLast4: '4408', cardLast4: '4471', plate: '品川 500-YY', base: 5800, discount: 0, final: 5800, authorized: true, manualToll: false },
  { icJa: ['東京', '仙台宮城'], icEn: ['Tokyo', 'Sendai-Miyagi'], entryTime: '05:50', exitTime: '10:35', vehicleClassJa: '普通車', vehicleClassEn: 'Standard', obuLast4: '7821', cardLast4: '3962', plate: '横浜 33-XX', base: 6800, discount: 680, final: 6120, authorized: true, manualToll: false },
  { icJa: ['吹田JCT', '広島'], icEn: ['Suita JCT', 'Hiroshima'], entryTime: '07:15', exitTime: '11:40', vehicleClassJa: '大型車', vehicleClassEn: 'Large', obuLast4: '9133', cardLast4: '4471', plate: '品川 500-YY', base: 11200, discount: 1120, final: 10080, authorized: true, manualToll: false },
  { icJa: ['横浜町田', '御殿場'], icEn: ['Yokohama-Machida', 'Gotemba'], entryTime: '15:42', exitTime: '16:58', vehicleClassJa: '普通車', vehicleClassEn: 'Standard', obuLast4: '4408', cardLast4: '4471', plate: '品川 500-YY', base: 2900, discount: 0, final: 2900, authorized: true, manualToll: false },

  // ── MANUAL HANDOFF (手渡し) — OBU & plate NOT recorded; card IS charged ──
  // Unauthorized + manual toll: the booth operator manually selected the
  // vehicle class, no wireless OBU read, no plate captured.
  { icJa: ['横浜町田', '名古屋'], icEn: ['Yokohama-Machida', 'Nagoya'], entryTime: '06:55', exitTime: '11:34', vehicleClassJa: '中型車', vehicleClassEn: 'Medium', obuLast4: '', cardLast4: '4471', plate: '', base: 6580, discount: 0, final: 6580, authorized: false, manualToll: true },
  { icJa: ['海老名', '小田原'], icEn: ['Ebina', 'Odawara'], entryTime: '13:22', exitTime: '14:04', vehicleClassJa: '普通車', vehicleClassEn: 'Standard', obuLast4: '', cardLast4: '6688', plate: '', base: 920, discount: 0, final: 920, authorized: false, manualToll: true },
];

const localize = (r, ja) => ({
  entryIc: ja ? r.icJa[0] : r.icEn[0],
  exitIc: ja ? r.icJa[1] : r.icEn[1],
  entryTime: r.entryTime,
  exitTime: r.exitTime,
  vehicleClass: ja ? r.vehicleClassJa : r.vehicleClassEn,
  obuLast4: r.obuLast4,
  cardLast4: r.cardLast4,
  plate: r.plate,
  base: r.base,
  discount: r.discount,
  final: r.final,
  authorized: r.authorized,
  manualToll: r.manualToll,
});

/**
 * tripCountForDays — how many sample rows to show for a given span.
 *
 * Short ranges stay short and scannable; long ranges surface a
 * substantial, realistic report (a full year ≈ ¥500,000 of activity)
 * so visitors see that JTR scales to heavy usage. Pure function of the
 * day count.
 */
function tripCountForDays(days) {
  if (days <= 1) return 2;
  if (days <= 7) return 5;
  if (days <= 31) return 8;
  if (days <= 92) return 18;   // ~quarter
  if (days <= 183) return 40;  // ~half year
  if (days <= 365) return 80;  // ~year
  return 130;                  // 15-month MEISAI window
}

/**
 * Build a list of sample trips for the chosen date range.
 *
 * Cycles deterministically through the template POOL, distributing the
 * trip dates evenly across the selected range. The number of rows
 * scales with the span (see tripCountForDays) so a one-year range
 * produces hundreds of thousands of yen of varied activity.
 *
 * Pure function — deterministic for a given (fromDate, toDate, ja).
 */
export function buildSampleTrips(fromDate, toDate, ja) {
  const from = new Date(fromDate);
  const to = toDate ? new Date(toDate) : from;
  if (Number.isNaN(from.getTime())) return [];
  const days = Math.max(0, Math.round((to - from) / 86400000)) + 1;
  const count = tripCountForDays(days);

  // For very short spans keep the original commute-first ordering so a
  // single day reads as "out + back".
  const pool = POOL.map((r) => localize(r, ja));
  const trips = [];
  for (let i = 0; i < count; i++) {
    // Cycle the pool, offset so consecutive rows vary (avoid 3 identical
    // commutes in a row on short spans).
    const tpl = pool[(i * 5 + (i % 3)) % pool.length];
    const fraction = count === 1 ? 0 : i / (count - 1);
    const tripMs = from.getTime() + fraction * (to.getTime() - from.getTime());
    const d = new Date(tripMs);
    trips.push({
      ...tpl,
      dateIso: d.toISOString().slice(0, 10),
      dateLabel: ja
        ? `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    });
  }
  return trips;
}

export function buildSummary(trips) {
  const totalTrips = trips.length;
  const totalBase = trips.reduce((s, t) => s + t.base, 0);
  const totalDiscount = trips.reduce((s, t) => s + t.discount, 0);
  const totalFinal = trips.reduce((s, t) => s + t.final, 0);
  const distinctCards = Array.from(new Set(trips.map((t) => t.cardLast4).filter(Boolean)));
  // Manual-handoff rows have no recorded plate, so they're excluded from
  // the distinct-vehicle tally (you can't identify a vehicle that was
  // never recorded).
  const distinctVehicles = Array.from(new Set(trips.map((t) => t.plate).filter(Boolean)));
  // Smart-audit aggregates surfaced in the email summary header.
  const authorizedCount = trips.filter((t) => t.authorized && !t.manualToll).length;
  const unauthorizedCount = trips.filter((t) => !t.authorized).length;
  const manualTollCount = trips.filter((t) => t.manualToll).length;
  const unauthorizedVehicles = Array.from(new Set(trips.filter((t) => !t.authorized).map((t) => t.plate).filter(Boolean)));
  return {
    totalTrips, totalBase, totalDiscount, totalFinal,
    distinctCards, distinctVehicles,
    authorizedCount, unauthorizedCount, manualTollCount, unauthorizedVehicles,
  };
}
